import { Route } from "../../../utils/models/Route";
import { distance as turfDistance, length as turfLength, lineString as turfLineString } from "@turf/turf";
import { elevationGainFromCoordinates } from "../../../utils/elevation/elevationGain";
import { LineStringFeature } from "../../../utils/models/LineStringFeature";

/** Guided/locked day-splitting clamps to the same range hike-planner uses. */
export const MIN_GUIDED_DAYS = 1;
export const MAX_GUIDED_DAYS = 30;

/** Below this, a dragged locked-mode boundary is treated as on-trail (no detour). */
export const ON_TRAIL_M = 30;

/**
 * A locked-mode day boundary: where day N ends / day N+1 begins along the
 * frozen master trail, plus an optional off-trail diversion (e.g. an
 * overnight stop). `detourLine` runs gate -> offTrailPoint (the "forward"
 * direction); the adjacent day reuses it reversed via LineStringFeature.reverse().
 */
export interface LockedBoundary {
  distanceM: number;
  offTrailPoint: number[] | null;
  detourLine: LineStringFeature | null;
}

export interface MasterTrail {
  /** Ordered [lon, lat, ele, ...] for the whole locked trail, one main-line route's worth of coordinates per day concatenated in day/position order. */
  coordinates: number[][];
  distanceMeters: number;
  elevationGainMeters: number;
}

export interface DaySlice {
  dayNumber: number;
  coordinates: number[][];
  distanceMeters: number;
  elevationGainMeters: number;
}

const isMainRouteStyle = (route: Route): boolean => !route.route_style || route.route_style === 'route';

function orderedMainRoutes(routes: Route[]): Route[] {
  return routes
    .filter(isMainRouteStyle)
    .filter(route => route.lineStrings.length > 0)
    .slice()
    .sort((a, b) => (a.day ?? 1) - (b.day ?? 1) || a.position - b.position);
}

function coordsOfRoute(route: Route): number[][] {
  const coords: number[][] = [];
  route.lineStrings.forEach(lineString => {
    const lsCoords = lineString.lineStringGeometry.coordinates;
    lsCoords.forEach((coord, idx) => {
      // Drop the point if it exactly repeats the line's own previous join point.
      if (idx === 0 && coords.length > 0) {
        const prev = coords[coords.length - 1];
        if (prev[0] === coord[0] && prev[1] === coord[1]) return;
      }
      coords.push(coord);
    });
  });
  return coords;
}

/**
 * Guided mode only makes sense as one continuous walking line per day — more
 * than one main-line ('route'-style) leg on the same day means a branch or
 * loop that can't be linearized into a single ordered sequence. Alt/spur/
 * transport legs are excluded from this check entirely (they aren't part of
 * the master trail either way).
 */
export function canDeriveMasterTrail(routes: Route[]): boolean {
  const mainRoutes = orderedMainRoutes(routes);
  if (mainRoutes.length === 0) return false;

  const perDayCount = new Map<number, number>();
  mainRoutes.forEach(route => {
    const day = route.day ?? 1;
    perDayCount.set(day, (perDayCount.get(day) ?? 0) + 1);
  });
  return ![...perDayCount.values()].some(count => count > 1);
}

/**
 * Concatenates every main-line ('route'-style) leg, in day/position order,
 * into one continuous trail. This is always DERIVED from current state, never
 * stored — re-locking after an unlock simply re-runs this over whatever main
 * legs exist at that moment (best-effort).
 */
export function deriveMasterTrail(routes: Route[]): MasterTrail {
  const mainRoutes = orderedMainRoutes(routes);
  const coordinates: number[][] = [];

  mainRoutes.forEach(route => {
    const routeCoords = coordsOfRoute(route);
    if (routeCoords.length === 0) return;

    if (coordinates.length === 0) {
      coordinates.push(...routeCoords);
      return;
    }

    const prevEnd = coordinates[coordinates.length - 1];
    const nextStart = routeCoords[0];
    const sharesEndpoint = prevEnd[0] === nextStart[0] && prevEnd[1] === nextStart[1];
    coordinates.push(...(sharesEndpoint ? routeCoords.slice(1) : routeCoords));
  });

  if (coordinates.length < 2) {
    return { coordinates, distanceMeters: 0, elevationGainMeters: 0 };
  }

  const distanceMeters = turfLength(turfLineString(coordinates.map(c => [c[0], c[1]])), { units: 'meters' });
  const elevationGainMeters = elevationGainFromCoordinates(coordinates);

  return { coordinates, distanceMeters, elevationGainMeters };
}

export function cumulativeDistances(coordinates: number[][]): number[] {
  const cumDist = [0];
  for (let i = 1; i < coordinates.length; i++) {
    const prev = coordinates[i - 1];
    const cur = coordinates[i];
    cumDist.push(cumDist[i - 1] + turfDistance([prev[0], prev[1]], [cur[0], cur[1]], { units: 'meters' }));
  }
  return cumDist;
}

function interpolateCoord(a: number[], b: number[], t: number): number[] {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    (a[2] ?? 0) + ((b[2] ?? 0) - (a[2] ?? 0)) * t,
  ];
}

/** Coordinate (with interpolated elevation) at `distance` metres along coordinates/cumDist. */
export function pointAtDistance(coordinates: number[][], cumDist: number[], distance: number): number[] {
  if (distance <= 0) return coordinates[0];
  const total = cumDist[cumDist.length - 1];
  if (distance >= total) return coordinates[coordinates.length - 1];

  for (let i = 1; i < cumDist.length; i++) {
    if (cumDist[i] >= distance) {
      const segStart = cumDist[i - 1];
      const segLen = cumDist[i] - segStart;
      const t = segLen === 0 ? 0 : (distance - segStart) / segLen;
      return interpolateCoord(coordinates[i - 1], coordinates[i], t);
    }
  }
  return coordinates[coordinates.length - 1];
}

export function sliceByDistance(coordinates: number[][], cumDist: number[], fromDist: number, toDist: number): number[][] {
  const result: number[][] = [pointAtDistance(coordinates, cumDist, fromDist)];
  for (let i = 0; i < coordinates.length; i++) {
    if (cumDist[i] > fromDist && cumDist[i] < toDist) {
      result.push(coordinates[i]);
    }
  }
  result.push(pointAtDistance(coordinates, cumDist, toDist));
  return result;
}

/**
 * Nearest VERTEX on the frozen trail to an arbitrary point (matching
 * hike-planner's nearestOnTrail — a vertex search, not a segment projection;
 * trail vertices are dense enough from routing that this is accurate).
 */
export function nearestOnTrail(
  coordinates: number[][],
  cumDist: number[],
  lon: number,
  lat: number
): { point: number[]; distanceM: number; offTrailM: number } {
  let bestIndex = 0;
  let bestD = Infinity;
  for (let i = 0; i < coordinates.length; i++) {
    const d = turfDistance([lon, lat], [coordinates[i][0], coordinates[i][1]], { units: 'meters' });
    if (d < bestD) {
      bestD = d;
      bestIndex = i;
    }
  }
  return { point: coordinates[bestIndex], distanceM: cumDist[bestIndex], offTrailM: bestD };
}

/**
 * Where a route branches off/rejoins the main line — its first marker if it
 * has one, else the first coordinate of its first lineString. Used to
 * regroup alt/spur legs onto whichever day their geometry now sits closest
 * to after the trail's day boundaries move (a day-count re-slice or a manual
 * boundary drag), rather than leaving them under a day number that no longer
 * means anything geographically.
 */
export function routeAnchorCoord(route: Route): number[] | null {
  const markerCoord = route.markers?.[0]?.markerGeometry?.coordinates;
  if (markerCoord && markerCoord.length >= 2) return markerCoord;
  const lineCoord = route.lineStrings?.[0]?.lineStringGeometry?.coordinates?.[0];
  return lineCoord ?? null;
}

/**
 * Which day (1..dayCount) a distance along the trail falls into, given the
 * day boundaries currently in effect — works equally for an even split
 * (guided-route.ts's applyDayCount) or arbitrary manually-dragged boundaries
 * (locked-boundary-drag.ts). `boundaries[d]` is where day `d` ends / day
 * `d + 1` begins, so a distance is in day `d` when it's at or before
 * `boundaries[d]`, else it falls through to later days (last day if it's
 * past every boundary).
 */
export function dayNumberForDistance(
  distanceM: number,
  boundaries: { [day: number]: LockedBoundary },
  dayCount: number
): number {
  for (let day = 1; day < dayCount; day++) {
    const boundary = boundaries[day];
    if (boundary && distanceM <= boundary.distanceM) return day;
  }
  return dayCount;
}

/** Even split of a master trail into `dayCount` day-slices, clamped to [MIN_GUIDED_DAYS, MAX_GUIDED_DAYS]. */
export function sliceMasterTrailIntoDays(trail: MasterTrail, dayCount: number): DaySlice[] {
  const n = Math.min(MAX_GUIDED_DAYS, Math.max(MIN_GUIDED_DAYS, Math.round(dayCount)));
  const { coordinates } = trail;
  if (coordinates.length < 2) return [];

  const cumDist = cumulativeDistances(coordinates);
  const total = cumDist[cumDist.length - 1];

  const slices: DaySlice[] = [];
  for (let i = 0; i < n; i++) {
    const from = (total * i) / n;
    const to = (total * (i + 1)) / n;
    const sliceCoords = sliceByDistance(coordinates, cumDist, from, to);
    slices.push({
      dayNumber: i + 1,
      coordinates: sliceCoords,
      distanceMeters: to - from,
      elevationGainMeters: elevationGainFromCoordinates(sliceCoords),
    });
  }
  return slices;
}
